import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { useScrollEndReached } from '../../hooks';
import { IInspectionItem } from '../../models/response/InspectionResponse';
import { useLazyFetchInspectionsListQuery } from '../../services/modules/inspection';
import { EmptyStateSvg } from '../../svg';
import { CarCard, Input, LoadingLottie } from '../../ui';

import { IInspectionList } from './InspectionList';
import { CardsContainer } from './InspectionListElements';

const LIMIT = 10;

export const TabHandler = (props: IInspectionList.ITabHandler) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const endPageRef = React.useRef<HTMLDivElement>(null);
  const hasEndReached = useScrollEndReached(endPageRef);

  const [inspectionList, setInspectionList] = useState<IInspectionItem[]>([]);
  const [offsetAll, setOffsetAll] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchListAll, { data: dataListAll, isFetching, isLoading }] =
    useLazyFetchInspectionsListQuery();

  const fetchAllList = async (search?: string, emptyList?: boolean) => {
    const params = {
      status: props.status === 'all' ? '' : props.status,
      search: search ? search : '',
      limit: LIMIT,
      offset: offsetAll,
    };
    const res = await fetchListAll(params);
    const data = res?.data;
    if (data) {
      props.setCount(data.count);
      setOffsetAll((state) => state + LIMIT);
      if (emptyList) {
        setInspectionList(data.results);
      } else {
        setInspectionList((prev) => [...prev, ...data.results]);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllList('');
  }, []);

  useEffect(() => {
    if (hasEndReached && dataListAll?.count !== inspectionList.length) {
      fetchAllList(searchValue);
    }
  }, [hasEndReached]);

  const debounce_search = useCallback(debounce(fetchAllList, 700), []);

  const onChangeSearch = (value: string) => {
    setSearchValue(value);
    setInspectionList([]);
    setLoading(true);
    debounce_search(value, true);
  };

  return (
    <>
      <Input
        label={t('inspection.search_here')}
        placeholder={t('inspection.search_placeholder')}
        marginWrapper={'0 12px 0 12px'}
        onClearSearch={() => onChangeSearch('')}
        onChange={(e) => onChangeSearch(e.currentTarget.value)}
      />
      <CardsContainer>
        {!loading && inspectionList.length === 0 && (
          <div className={'tw-flex tw-flex-col tw-items-center'}>
            <EmptyStateSvg />
            <p className={'tw-text-gray0 tw-font-bold tw-text-xl'}>
              {t('inspection.empty_state_message')}
            </p>
          </div>
        )}
        {inspectionList.map((item) => (
          <CarCard key={item.id} item={item} onClick={() => props.onClickItem(item)} />
        ))}
        <div ref={endPageRef} />
        {loading && <LoadingLottie />}
      </CardsContainer>
    </>
  );
};
